"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDropPollOption } from "@/generated/models/ApiDropPollOption";
import { usePrefetchDropPollOptionVoters } from "@/hooks/useDropPollOptionVoters";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { type MouseEvent, useEffect, useState } from "react";
import { getVoteCountLabel } from "./WaveDropPoll.helpers";
import { PollOptionVoterPreviews } from "./PollOptionVoters";
interface PollResultOptionProps {
  readonly dropId: string;
  readonly option: ApiDropPollOption;
  readonly totalVotes: number;
  readonly isSelected: boolean;
  readonly isDimmed: boolean;
  readonly isExpanded: boolean;
  readonly multichoice: boolean;
  readonly showSelectionIndicator: boolean;
  readonly onToggle: (optionNo: number) => void;
}
const getPollResultButtonClassName = ({
  isSelected,
  isDimmed
}: {
  readonly isSelected: boolean;
  readonly isDimmed: boolean;
}): string => {
  const selectionClassName = isSelected ? "tw-border-iron-600 tw-bg-iron-800 tw-shadow-[0_0_15px_rgba(245,245,245,0.04)]" : "tw-border-iron-700 tw-bg-iron-800/60 desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-800/80";
  const opacityClassName = isDimmed ? "tw-opacity-80 desktop-hover:hover:tw-opacity-100" : "tw-opacity-100";
  return `tw-group/result tw-relative tw-flex tw-min-h-11 tw-w-full tw-transform-gpu tw-cursor-pointer tw-items-stretch tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-p-0 tw-text-left tw-outline-none tw-transition-all tw-duration-300 focus-visible:tw-ring-2 focus-visible:tw-ring-white/30 ${selectionClassName} ${opacityClassName}`;
};
const getSelectionIndicatorStateClassName = (isSelected: boolean): string => isSelected ? "tw-scale-110 tw-border-iron-50 tw-bg-iron-50 desktop-hover:group-hover/result:tw-scale-100 desktop-hover:group-hover/result:tw-border-iron-200 desktop-hover:group-hover/result:tw-bg-iron-200" : "tw-border-iron-600 tw-bg-iron-900/50 desktop-hover:group-hover/result:tw-border-iron-400";
const getSelectionGlyphStateClassName = ({
  isSelected,
  multichoice
}: {
  readonly isSelected: boolean;
  readonly multichoice: boolean;
}): string => {
  if (isSelected) {
    return "tw-scale-100 tw-opacity-100";
  }
  return multichoice ? "tw-scale-50 tw-opacity-0" : "tw-scale-0 tw-opacity-0";
};
function PollResultSelectionIndicator(t0) {
  const $ = _c(11);
  const {
    isSelected,
    multichoice
  } = t0;
  const indicatorShapeClass = multichoice ? "tw-rounded" : "tw-rounded-full";
  let t1;
  if ($[0] !== isSelected) {
    t1 = getSelectionIndicatorStateClassName(isSelected);
    $[0] = isSelected;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const indicatorStateClass = t1;
  let t2;
  if ($[2] !== isSelected || $[3] !== multichoice) {
    t2 = getSelectionGlyphStateClassName({
      isSelected,
      multichoice
    });
    $[2] = isSelected;
    $[3] = multichoice;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const glyphStateClass = t2;
  const t3 = `tw-mt-0.5 tw-flex tw-size-4 tw-shrink-0 tw-items-center tw-justify-center tw-border tw-border-solid tw-transition-all tw-duration-300 ${indicatorShapeClass} ${indicatorStateClass}`;
  let t4;
  if ($[5] !== glyphStateClass || $[6] !== multichoice) {
    t4 = multichoice ? <CheckIcon className={`tw-size-2.5 tw-text-iron-950 tw-transition-all tw-duration-300 ${glyphStateClass}`} strokeWidth={3} /> : <span className={`tw-size-1.5 tw-rounded-full tw-bg-iron-950 tw-transition-all tw-duration-300 ${glyphStateClass}`} />;
    $[5] = glyphStateClass;
    $[6] = multichoice;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <span className={t3} aria-hidden="true">{t4}</span>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function PollResultOptionLabel(t0) {
  const $ = _c(3);
  const {
    isSelected,
    optionString
  } = t0;
  const labelClassName = isSelected ? "tw-font-medium tw-text-iron-50" : "tw-font-medium tw-text-iron-100 desktop-hover:group-hover/result:tw-text-iron-50";
  const t1 = `tw-min-w-0 tw-break-words tw-text-[13px] tw-leading-5 tw-transition-colors tw-duration-300 ${labelClassName}`;
  let t2;
  if ($[0] !== optionString || $[1] !== t1) {
    t2 = <span className={t1}>{optionString}</span>;
    $[0] = optionString;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function PollResultOptionStats(t0) {
  const $ = _c(14);
  const {
    animateIn,
    dropId,
    isExpanded,
    isSelected,
    option,
    percentage
  } = t0;
  const statsStateClass = animateIn ? "tw-translate-x-0 tw-opacity-100" : "tw-translate-x-4 tw-opacity-0";
  const voteCountClassName = isSelected ? "tw-text-iron-200" : "tw-text-iron-300 desktop-hover:group-hover/result:tw-text-iron-200";
  const percentageClassName = isSelected ? "tw-text-iron-100" : "tw-text-iron-200 desktop-hover:group-hover/result:tw-text-iron-100";
  const chevronClassName = isExpanded ? "tw-rotate-180" : "";
  const t1 = `tw-ml-2 tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2.5 tw-pt-0.5 tw-transition-all tw-duration-500 ${statsStateClass}`;
  let t2;
  if ($[0] !== dropId || $[1] !== option || $[2] !== voteCountClassName) {
    t2 = option.votes > 0 && <span className="tw-flex tw-items-center tw-gap-1.5"><PollOptionVoterPreviews dropId={dropId} option={option} /><span className={`tw-text-[11.5px] tw-font-medium tw-transition-colors ${voteCountClassName}`}>{getVoteCountLabel(option.votes)}</span></span>;
    $[0] = dropId;
    $[1] = option;
    $[2] = voteCountClassName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `tw-w-8 tw-text-right tw-text-xs tw-font-bold tw-transition-colors ${percentageClassName}`;
  let t4;
  if ($[4] !== percentage || $[5] !== t3) {
    t4 = <span className={t3}>{percentage}%</span>;
    $[4] = percentage;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `tw-size-3.5 tw-flex-shrink-0 tw-text-iron-600 tw-transition-transform tw-duration-200 desktop-hover:group-hover/result:tw-text-iron-400 ${chevronClassName}`;
  let t6;
  if ($[7] !== t5) {
    t6 = <ChevronDownIcon className={t5} aria-hidden="true" />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className={t1}>{t2}{t4}{t6}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function PollResultOption(t0) {
  const $ = _c(52);
  const {
    dropId,
    option,
    totalVotes,
    isSelected,
    isDimmed,
    isExpanded,
    multichoice,
    showSelectionIndicator,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== option.votes || $[1] !== totalVotes) {
    t1 = totalVotes > 0 ? Math.round(option.votes / totalVotes * 100) : 0;
    $[0] = option.votes;
    $[1] = totalVotes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const percentage = t1;
  const fillScale = Math.max(0, Math.min(100, percentage)) / 100;
  const [animateIn, setAnimateIn] = useState(false);
  const prefetchVoters = usePrefetchDropPollOptionVoters();
  let t2;
  if ($[3] !== dropId || $[4] !== option.option_no || $[5] !== option.votes || $[6] !== prefetchVoters) {
    t2 = () => prefetchVoters({
      dropId,
      optionNo: option.option_no,
      enabled: option.votes > 0
    });
    $[3] = dropId;
    $[4] = option.option_no;
    $[5] = option.votes;
    $[6] = prefetchVoters;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handlePrefetchVoters = t2;
  let t3;
  if ($[8] !== onToggle || $[9] !== option.option_no) {
    t3 = event => {
      event.stopPropagation();
      onToggle(option.option_no);
    };
    $[8] = onToggle;
    $[9] = option.option_no;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleToggle = t3;
  let t4;
  if ($[11] !== isDimmed || $[12] !== isSelected) {
    t4 = getPollResultButtonClassName({
      isSelected,
      isDimmed
    });
    $[11] = isDimmed;
    $[12] = isSelected;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const buttonClassName = t4;
  const fillClassName = isSelected ? "tw-bg-iron-700" : "tw-bg-iron-800 desktop-hover:group-hover/result:tw-bg-iron-700";
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setAnimateIn(false);
      let secondFrame = 0;
      const firstFrame = globalThis.requestAnimationFrame(() => {
        secondFrame = globalThis.requestAnimationFrame(() => {
          setAnimateIn(true);
        });
      });
      return () => {
        globalThis.cancelAnimationFrame(firstFrame);
        globalThis.cancelAnimationFrame(secondFrame);
      };
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== fillScale) {
    t6 = [fillScale];
    $[15] = fillScale;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t5, t6);
  const t7 = option.option_string;
  let t8;
  if ($[17] !== option.votes) {
    t8 = getVoteCountLabel(option.votes);
    $[17] = option.votes;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = `${isExpanded ? "Hide" : "View"} voters for ${t7}. ${t8}, ${percentage} percent.`;
  const t10 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-full tw-origin-left tw-transform-gpu tw-transition-[transform,background-color] tw-duration-700 tw-ease-out ${fillClassName}`;
  const t11 = `scaleX(${animateIn ? fillScale : 0})`;
  let t12;
  if ($[19] !== t11) {
    t12 = {
      transform: t11
    };
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t12) {
    t13 = <div className={t10} style={t12} aria-hidden="true" />;
    $[21] = t10;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isSelected || $[25] !== multichoice || $[26] !== showSelectionIndicator) {
    t14 = showSelectionIndicator && <PollResultSelectionIndicator isSelected={isSelected} multichoice={multichoice} />;
    $[24] = isSelected;
    $[25] = multichoice;
    $[26] = showSelectionIndicator;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isSelected || $[29] !== option.option_string) {
    t15 = <PollResultOptionLabel isSelected={isSelected} optionString={option.option_string} />;
    $[28] = isSelected;
    $[29] = option.option_string;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2.5">{t14}{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== animateIn || $[35] !== dropId || $[36] !== isExpanded || $[37] !== isSelected || $[38] !== option || $[39] !== percentage) {
    t17 = <PollResultOptionStats animateIn={animateIn} dropId={dropId} isExpanded={isExpanded} isSelected={isSelected} option={option} percentage={percentage} />;
    $[34] = animateIn;
    $[35] = dropId;
    $[36] = isExpanded;
    $[37] = isSelected;
    $[38] = option;
    $[39] = percentage;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t16 || $[42] !== t17) {
    t18 = <div className="tw-relative tw-flex tw-w-full tw-min-w-0 tw-items-start tw-justify-between tw-gap-3 tw-px-3.5 tw-py-3">{t16}{t17}</div>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== buttonClassName || $[45] !== handlePrefetchVoters || $[46] !== handleToggle || $[47] !== isExpanded || $[48] !== t13 || $[49] !== t18 || $[50] !== t9) {
    t19 = <div className="tw-overflow-hidden tw-rounded-lg"><button type="button" aria-expanded={isExpanded} aria-label={t9} onClick={handleToggle} onFocus={handlePrefetchVoters} onPointerEnter={handlePrefetchVoters} className={buttonClassName}>{t13}{t18}</button></div>;
    $[44] = buttonClassName;
    $[45] = handlePrefetchVoters;
    $[46] = handleToggle;
    $[47] = isExpanded;
    $[48] = t13;
    $[49] = t18;
    $[50] = t9;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
}